import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/paysuccess.css";

const ORDER_STORAGE_KEY = "storefront:lastOrder";

const money = new Intl.NumberFormat(siteConfig.locale || "en-US", {
  style: "currency",
  currency: siteConfig.currency || "USD",
});

const formatDate = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "To be confirmed";
  return new Intl.DateTimeFormat("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

const PaySuccess = () => {
  const location = useLocation();
  const [storedOrder] = useState(() => {
    try {
      const value = window.sessionStorage.getItem(ORDER_STORAGE_KEY);
      return value ? JSON.parse(value) : null;
    } catch {
      return null;
    }
  });
  const order = location.state?.order || storedOrder;
  const supportEmail = siteConfig.contact?.email;

  if (!order) {
    return (
      <main className="confirmation-page confirmation-page--empty">
        <section
          className="confirmation-empty"
          aria-labelledby="confirmation-empty-title"
        >
          <span className="confirmation-eyebrow">Order status</span>
          <h1 id="confirmation-empty-title">No recent order found.</h1>
          <p>
            When you place an order with {siteConfig.brandName}, its
            confirmation will appear here.
          </p>
          <Link className="confirmation-button" to="/shop">
            Explore the shop
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="confirmation-page">
      <div className="confirmation-shell">
        <section className="confirmation-card" aria-labelledby="confirmation-title">
          <div className="confirmation-card__mark" aria-hidden="true">
            <svg viewBox="0 0 24 24" focusable="false">
              <path d="m5 12.5 4.25 4.25L19 7" />
            </svg>
          </div>
          <span className="confirmation-eyebrow">Order confirmed</span>
          <h1 id="confirmation-title">Thank you. We’ve received your order.</h1>
          <p className="confirmation-card__intro">
            Your {siteConfig.brandName} order is confirmed.
          </p>

          <div className="confirmation-number">
            <span>Order reference</span>
            <strong>{order.number}</strong>
          </div>

          <dl className="confirmation-details">
            <div>
              <dt>Order date</dt>
              <dd>
                <time dateTime={order.placedAt}>{formatDate(order.placedAt)}</time>
              </dd>
            </div>
            <div>
              <dt>Items</dt>
              <dd>{order.itemCount}</dd>
            </div>
            {Number(order.discount) > 0 && (
              <div>
                <dt>Promotion</dt>
                <dd>−{money.format(Number(order.discount))}</dd>
              </div>
            )}
            <div>
              <dt>Shipping</dt>
              <dd>
                {Number(order.shipping) === 0
                  ? "Free"
                  : money.format(Number(order.shipping))}
              </dd>
            </div>
            <div className="confirmation-details__total">
              <dt>Order total</dt>
              <dd>{money.format(Number(order.total) || 0)}</dd>
            </div>
          </dl>

          <div className="confirmation-delivery">
            <span className="confirmation-delivery__icon" aria-hidden="true">
              ↗
            </span>
            <div>
              <strong>Estimated standard delivery</strong>
              <time dateTime={order.estimatedDelivery}>
                By {formatDate(order.estimatedDelivery)}
              </time>
            </div>
          </div>

          <div className="confirmation-actions">
            <Link className="confirmation-button" to="/shop">
              Continue shopping
            </Link>
            <Link className="confirmation-link" to="/shipping">
              Shipping information
            </Link>
          </div>
        </section>

        <aside className="confirmation-help" aria-labelledby="confirmation-help-title">
          <span className="confirmation-help__icon" aria-hidden="true">
            ?
          </span>
          <div>
            <h2 id="confirmation-help-title">Questions about your order?</h2>
            <p>
              Our customer care team is happy to help with product, delivery,
              and gifting questions.
            </p>
            <div className="confirmation-help__links">
              {supportEmail && <a href={`mailto:${supportEmail}`}>{supportEmail}</a>}
              <Link to="/contact">Contact us</Link>
            </div>
          </div>
        </aside>
      </div>
    </main>
  );
};

export default PaySuccess;
